import React from 'react';
import { createTheme, styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import Switch, { switchClasses } from '@mui/material/Switch';
import {
	Avatar, Badge, Box, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Stack, TextField, Typography
} from '@mui/material';
import Divider from '@mui/material/Divider';
import { MRT_EditActionButtons } from 'material-react-table';
import { FielderHard, FielderPink } from '@core/theme/baseTheme';

const pxToRem = (px) => `${px / 17}rem`;

// Themes
export const createSmartFielderTheme = (mode = 'light') => {
	const isDark = mode === 'dark';
	const dividerColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(224, 224, 224, 0.6)';
	const theme = createTheme({
		palette: {
			mode,
			...(mode === 'dark' && {
				background: { default: '#121212', paper: '#1e1e1e' }
			}),
			primary: { main: FielderHard },
		},
		typography: {
			fontFamily: 'Lato, sans-serif',
			fontSize: 13,
			h1: {
				color: '#a1a1a1',
				fontSize: '1rem',
				fontWeight: 400,
				letterSpacing: '0.7px',
			},
			h2: {
				color: '#a1a1a1',
				fontSize: '2rem',
				fontWeight: 400,
			},
		},
		components: {
			MuiCssBaseline: {
				styleOverrides: {
					body: {
						fontFamily: "Lato, 'Roboto', 'Helvetica', 'Arial', sans-serif",
					},
					'*, *::before, *::after': {
						boxSizing: 'border-box',
						fontFamily: 'Lato, sans-serif',
					},
					// --- Global Form Control Dark/Light Mode Overrides ---
					'.EditForm-Signature-canvas': {
						width: '85%',
						height: '90%',
						border: `0.5px solid #979797`,
					},
					'.form-control': {
						backgroundColor: isDark ? '#2c2c2c !important' : '#fff !important',
						color: isDark ? '#fff !important' : '#000 !important',
					},
					'.form-control:focus': {
						color: isDark ? '#fff !important' : '#000 !important',
					},
					'.form-control:disabled, .form-control[readonly]': {
						backgroundColor: isDark ? '#2c2c2c !important' : '#fff !important',
						color: isDark ? '#fff !important' : '#000 !important',
					},
					// --- React DatePicker Dark/Light Mode Overrides ---
					'.rdtPicker': {
						backgroundColor: isDark ? '#2c2c2c !important' : '#fff !important',
						color: isDark ? '#fff !important' : '#000 !important',
						border: `1px solid ${isDark ? '#555' : '#ccc'}`,
						boxShadow: isDark
							? '0 2px 6px rgba(0,0,0,0.5)'
							: '0 2px 6px rgba(0,0,0,0.1)',
					},
					'.rdtDay, .rdtMonth, .rdtYear, .rdtTimeToggle': {
						color: isDark ? '#fff' : '#000',
					},
					'.rdtDay:hover, .rdtMonth:hover, .rdtYear:hover, .rdtActive, rdtBtn:hover': {
						backgroundColor: FielderHard,
						color: '#fff',
					},
					'.rdtCounter .rdtBtn:hover': {
						background: FielderHard,
					},
					'.rdtDisabled': {
						color: isDark ? '#777' : '#bbb',
						cursor: 'not-allowed',
					},
					'.rdtCounter': {
						color: isDark ? '#fff' : '#000',
					},
					'.rdtTime td': {
						color: isDark ? '#fff' : '#000',
					},
					// --- Ant Design DatePicker Dark/Light Mode Overrides ---
					'.ant-picker': {
						backgroundColor: isDark ? '#1e1e1e' : '#ffffff',
						borderColor: isDark ? '#555' : '#d9d9d9',
						color: isDark ? '#f5f5f5' : '#000000',
						borderRadius: '8px',
						transition: 'all 0.2s ease-in-out',
					},
					'.ant-picker .ant-picker-suffix': {
						color: isDark ? '#fff' : '#000',
					},
					'.ant-picker .ant-picker-clear': {
						color: FielderHard,
					},
					'.ant-picker .ant-picker-clear:hover': {
						color: FielderHard,
					},
					'.ant-picker-outlined:hover': {
						backgroundColor: FielderHard,
						background: 'none',
					},
					'.ant-picker-input > input': {
						color: isDark ? '#f5f5f5' : '#000000',
						backgroundColor: 'transparent',
						'&::placeholder': {
							color: isDark ? '#9e9e9e !important' : '#8c8c8c !important',
							opacity: 1,
						},
					},
					'.ant-picker-separator': {
						color: isDark ? '#bdbdbd' : '#595959',
					},
					'.ant-picker-suffix': {
						color: isDark ? '#bdbdbd' : '#595959',
					},
					// --- ✅ Charts/Legends inside <svg> (text + bullet) ---
					'svg g[aria-label] text': {
						fill: `${isDark ? '#e0e0e0' : '#666'} !important`,
					},
					'svg g:hover text': {
						fill: `${isDark ? FielderHard : '#333'} !important`,
					},
					/* --- Global DnD styles (Dark/Light aware) --- */
					'.smart-droppable': {
						borderRadius: 8,
						padding: 8,
						transition: 'background-color 0.25s ease, border-color 0.25s ease',
						backgroundColor: isDark ? '#121212' : '#ffffff',
						border: `1px dashed ${isDark ? '#555' : '#d0d0d0'}`,
					},
					'.smart-droppable.dnd-drag-over': {
						backgroundColor: isDark
							? 'rgba(220,92,135,0.25)'
							: 'rgba(220,92,135,0.15)',
						borderColor: FielderHard,
					},

					'.smart-draggable': {
						borderRadius: 6,
						padding: 8,
						marginBottom: 6,
						backgroundColor: isDark ? '#1e1e1e' : '#f5f5f5',
						color: isDark ? '#ffffff' : '#000000',
						transition: 'background-color .25s ease, box-shadow .25s ease',
					},
					'.smart-draggable.dnd-is-dragging': {
						backgroundColor: FielderHard,
						color: '#ffffff',
						boxShadow: '0 4px 10px rgba(0,0,0,0.35)',
					},
					// --- Cleave (date/time) inputs ---
					'.cleave-input': {
						backgroundColor: isDark ? '#1e1e1e !important' : '#ffffff !important',
						color: isDark ? '#ffffff !important' : '#000000 !important',
						borderRadius: 8,
						border: `1px solid ${isDark ? '#555 !important' : '#ccc !important'}`,
						padding: '6px 10px',
						fontSize: 13,
						transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
						outline: 'none',
						'&::placeholder': {
							color: isDark ? '#9e9e9e !important' : '#757575 !important',
							opacity: 1,
						},
						'&:disabled': {
							backgroundColor: isDark ? '#2c2c2c !important' : '#f5f5f5 !important',
							color: isDark ? '#bdbdbd !important' : '#9e9e9e !important',
							cursor: 'not-allowed',
						},
						'&:focus': {
							borderColor: `${FielderHard} !important`,
							boxShadow: `0 0 0 1px ${FielderHard} !important`,
						},
					},
					'.cleave-input.AddForm-border-red': {
						borderColor: '#f44336',
						boxShadow: '0 0 0 1px #f44336',
					},

					// --- react-select (con classNamePrefix="smart-select") ---
					'.smart-select__control': {
						backgroundColor: isDark ? '#1e1e1e !important' : '#ffffff !important',
						borderColor: isDark ? '#555 !important' : '#e0e0e0 !important',
						minHeight: 38,
						boxShadow: 'none',
						'&:hover': {
							borderColor: FielderHard,
						},
					},
					'.smart-select__control--is-focused': {
						borderColor: FielderHard,
						boxShadow: `0 0 0 1px ${FielderHard}`,
					},
					'.smart-select__value-container': {
						color: isDark ? '#dd0606 !important' : '#000000 !important',
						fontSize: 13,
					},
					'.smart-select__single-value': {
						color: isDark ? '#ffffff !important' : '#000000 !important',
					},
					'.smart-select__placeholder': {
						color: isDark ? '#bdbdbd !important' : '#757575 !important',
					},
					'.smart-select__menu': {
						backgroundColor: isDark ? '#232323 !important' : '#ffffff !important',
						color: isDark ? '#ffffff !important' : '#000000 !important',
						zIndex: 1400,
					},
					'.smart-select__menu-list': {
						backgroundColor: isDark ? '#232323 !important' : '#ffffff !important',
					},
					'.smart-select__option': {
						backgroundColor: 'transparent',
						color: isDark ? '#ffffff !important' : '#000000 !important',
						cursor: 'pointer',
					},
					'.smart-select__option--is-selected': {
						backgroundColor: `${FielderPink} !important`,
						color: '#ffffff',
					},
					'.smart-select__option--is-focused': {
						backgroundColor: `${FielderHard} !important`,
						color: '#ffffff',
					},
					'.smart-select__indicator-separator': {
						backgroundColor: isDark ? '#444444 !important' : '#e0e0e0 !important',
					},
					'.smart-select__dropdown-indicator, .smart-select__clear-indicator': {
						color: isDark ? '#bbbbbb !important' : '#666666 !important',
					},
					'.smart-select__clear-indicator:hover': {
						color: `${FielderHard} !important`,
					},
					'.smart-select__dropdown-indicator:hover': {
						color: `${FielderHard} !important`,
					},
					'.smart-select.smart-select-error .smart-select__control': {
						borderColor: '#f44336 !important',
						boxShadow: '0 0 0 1px #f44336 !important',
					},
					// ========== Ant Design TreeSelect (antd) ==========
					// Root wrapper
					'.ant-select.ant-tree-select': {
						fontSize: 13,
					},
					// Selector (the visible input area)
					'.ant-select.ant-tree-select .ant-select-selector': {
						backgroundColor: isDark
							? '#1e1e1e !important'
							: '#ffffff !important',
						borderColor: isDark
							? '#555555 !important'
							: '#d9d9d9 !important',
						color: isDark
							? '#f5f5f5 !important'
							: '#000000 !important',
						borderRadius: 8,
						minHeight: 38,
						padding: '4px 8px',
						boxShadow: 'none !important',
						transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
					},
					// Hover / focus state
					'.ant-select.ant-tree-select.ant-select-focused .ant-select-selector, .ant-select.ant-tree-select .ant-select-selector:hover': {
						borderColor: `${FielderHard} !important`,
						boxShadow: `0 0 0 1px ${FielderHard} !important`,
					},
					// Placeholder
					'.ant-select.ant-tree-select .ant-select-selection-placeholder': {
						color: isDark
							? '#9e9e9e !important'
							: '#8c8c8c !important',
					},
					// Selected values (tags in multiple mode)
					'.ant-select.ant-tree-select .ant-select-selection-item': {
						color: isDark ? '#ffffff !important' : '#000000 !important',
						//backgroundColor: isDark ? '#2b2b2b !important' : '#f5f5f5 !important',
						//borderRadius: 6,
						//border: `1px solid ${isDark ? '#555555 !important' : '#d9d9d9 !important'}`,
					},
					// Search input inside the selector
					'.ant-select.ant-tree-select .ant-select-selection-search-input': {
						backgroundColor: 'transparent !important',
						color: isDark
							? '#ffffff !important'
							: '#000000 !important',
					},
					// Arrow & clear icons
					'.ant-select.ant-tree-select .ant-select-arrow, .ant-select.ant-tree-select .ant-select-clear': {
						color: isDark
							? '#bdbdbd !important'
							: '#595959 !important',
						transition: 'color 0.2s ease',
					},
					'.ant-select.ant-tree-select .ant-select-arrow:hover, .ant-select.ant-tree-select .ant-select-clear:hover': {
						color: `${FielderHard} !important`,
					},
					// Disabled state
					'.ant-select.ant-tree-select.ant-select-disabled .ant-select-selector': {
						backgroundColor: isDark
							? '#2c2c2c !important'
							: '#f5f5f5 !important',
						color: isDark
							? '#bdbdbd !important'
							: '#9e9e9e !important',
						cursor: 'not-allowed !important',
					},
					'.ant-select.ant-tree-select.ant-select-disabled .ant-select-arrow': {
						color: isDark
							? '#555555 !important'
							: '#bfbfbf !important',
					},
					// ===== Dropdown panel =====
					'.ant-select-dropdown': {
						backgroundColor: isDark
							? '#232323 !important'
							: '#ffffff !important',
						color: isDark
							? '#f5f5f5 !important'
							: '#000000 !important',
						borderRadius: 8,
						boxShadow: isDark
							? '0 4px 10px rgba(0,0,0,0.6) !important'
							: '0 4px 10px rgba(0,0,0,0.15) !important',
						overflow: 'hidden',
					},
					// Tree container
					'.ant-select-tree': {
						backgroundColor: 'transparent !important',
						color: isDark
							? '#e0e0e0 !important'
							: '#000000 !important',
					},
					// Node text
					'.ant-select-tree .ant-select-tree-node-content-wrapper': {
						padding: '4px 8px',
						borderRadius: 4,
						transition: 'background-color 0.15s ease, color 0.15s ease',
						color: isDark
							? '#e0e0e0 !important'
							: '#000000 !important',
					},
					'.ant-select-tree .ant-select-tree-node-content-wrapper:hover': {
						backgroundColor: isDark
							? 'rgba(220,92,135,0.25) !important'
							: 'rgba(220,92,135,0.12) !important',
						color: isDark
							? '#ffffff !important'
							: '#000000 !important',
					},
					// Selected node
					'.ant-select-tree .ant-select-tree-node-selected .ant-select-tree-node-content-wrapper': {
						backgroundColor: `${FielderHard} !important`,
						color: '#ffffff !important',
					},
					// Checkboxes (checkable TreeSelect)
					'.ant-select-tree-checkbox-inner': {
						backgroundColor: isDark
							? '#1e1e1e !important'
							: '#ffffff !important',
						borderColor: isDark
							? '#666666 !important'
							: '#bfbfbf !important',
						borderRadius: 4,
						width: 14,
						height: 14,
					},
					'.ant-select-tree-checkbox-checked .ant-select-tree-checkbox-inner': {
						backgroundColor: `${FielderHard} !important`,
						borderColor: `${FielderHard} !important`,
					},
					'.ant-select-tree-checkbox-checked .ant-select-tree-checkbox-inner::after': {
						borderColor: '#ffffff !important',
					},
					// Multiple-select: remove button on selected tags
					':where(.css-dev-only-do-not-override-cg4vza).ant-select-multiple .ant-select-selection-item-remove': {
						color: isDark ? '#ffffff !important' : '#121212 !important',
					},
					':where(.css-dev-only-do-not-override-cg4vza).ant-select-multiple .ant-select-selection-item-remove:hover': {
						color: `${FielderHard} !important`,
					},
					// Single-select / normal selected node
					':where(.css-dev-only-do-not-override-cg4vza).ant-tree-select-dropdown .ant-select-tree .ant-select-tree-node-content-wrapper.ant-select-tree-node-selected':
						{
							backgroundColor: `${FielderHard} !important`,
							color: '#ffffff !important',
						},
					// Checkable TreeSelect: selected label after checkbox
					':where(.css-dev-only-do-not-override-cg4vza).ant-tree-select-dropdown .ant-select-tree .ant-select-tree-checkbox+span.ant-select-tree-node-selected':
						{
							backgroundColor: isDark
								? `${FielderHard} !important`
								: `${FielderPink} !important`,
							color: '#ffffff !important',
						},
					// (Optional) keep hover style consistent with selection when hovered
					':where(.css-dev-only-do-not-override-cg4vza).ant-tree-select-dropdown .ant-select-tree .ant-select-tree-node-content-wrapper.ant-select-tree-node-selected:hover':
						{
							backgroundColor: isDark
								? `${FielderHard} !important`
								: `${FielderPink} !important`,
						},
					// ========== /Ant Design TreeSelect ==========
				},
			},
			MuiTableCell: {
				styleOverrides: {
					root: {
						padding: '4px 8px',
						borderBottom: `1px solid ${dividerColor}`,
					},
					head: {
						fontWeight: 600,
						fontSize: '0.73rem',
						lineHeight: 1.2,
						textAlign: 'center',
						fontFamily: 'Lato, sans-serif',
						py: '6px',
					},
					body: {
						fontSize: '0.72rem',
						lineHeight: 1.2,
						fontFamily: 'Lato, sans-serif',
						py: '6px',
					},
				},
			},
			MuiToolbar: {
				styleOverrides: {
					root: {
						minHeight: '40px !important',
						padding: '0 8px',
						display: 'flex',
						justifyContent: 'space-between',
						gap: '8px',
					},
				},
			},
			MuiTableContainer: {
				styleOverrides: {
					root: {
						maxHeight: 500,
						overflowY: 'auto',
					},
				},
			},
			MuiIconButton: {
				styleOverrides: {
					root: {
						//color: '#dc5c87',
						'&.Mui-disabled': {
							color: 'rgba(0,0,0,0.26)',
						},
						'&:hover': {
							color: FielderHard,
						},
					},
				},
			},
			MuiTooltip: {
				styleOverrides: {
					tooltip: {
						fontSize: '0.75rem',
					},
				},
			},
			MuiRadio: {
				styleOverrides: {
					root: {
						'&.Mui-checked': { color: FielderHard },
						'&.Mui-focusVisible': { outline: `2px solid ${FielderHard}`, outlineOffset: 2, borderRadius: '50%' },
					},
				},
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: ({ ownerState }) => {
						let radius = 10;
						if (ownerState.size === 'small') radius = 8;
						else if (ownerState.size === 'large') radius = 12;
						return {
							borderRadius: radius,
							'& .MuiOutlinedInput-notchedOutline': { borderRadius: radius, },
							'&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: FielderHard },
							'&:hover .MuiOutlinedInput-notchedOutline': { borderColor: FielderHard },
						};
					},
				},
			},
			MuiFormLabel: {
				styleOverrides: { root: { '&.Mui-focused': { color: FielderHard } } },
			},
			MuiPaper: {
				styleOverrides: {
					root: {

						backgroundImage: mode === 'dark'
							? 'none'
							: 'default',
					},
				},
			},
			MuiNativeSelect: {
				styleOverrides: {
					select: ({ theme }) => ({
						backgroundColor: theme.palette.background.paper,
						color: theme.palette.text.primary,
						'& option': {
							backgroundColor: theme.palette.background.paper,
							color: theme.palette.text.primary,
						},
						'&:focus': {
							backgroundColor: 'transparent',
						},
					}),
					icon: ({ theme }) => ({
						color: theme.palette.text.primary,
					})
				}
			},
			MuiMenu: {
				styleOverrides: {
					paper: ({ theme }) => ({
						backgroundColor: theme.palette.background.paper,
						color: theme.palette.text.primary,
						boxShadow: theme.shadows[3],
					}),
				},
			},
		},
	});
	theme.custom = {
		...(theme.custom || {}),
		getReactSelectStyles: () => {
			const isDark = theme.palette.mode === 'dark';
			const paperBg = isDark ? '#1e1e1e' : '#fff';
			const listBg  = isDark ? '#232323' : '#fff';
			const hoverBg = theme.palette.primary.main;
			return {
				container: (base) => ({ ...base, fontSize: 14 }),
				control: (base, state) => ({
					...base,
					backgroundColor: paperBg,
					borderColor: state.isFocused
						? theme.palette.primary.main
						: isDark ? '#555' : '#ccc',
					color: isDark ? '#fff' : '#000',
					boxShadow: state.isFocused ? `0 0 0 1px ${theme.palette.primary.main}` : 'none',
					'&:hover': { borderColor: theme.palette.primary.main },
					minHeight: 38,
				}),
				valueContainer: (base) => ({ ...base, color: isDark ? '#fff' : '#000' }),
				input: (base) => ({ ...base, color: isDark ? '#fff' : '#000' }),
				placeholder: (base) => ({ ...base, color: isDark ? '#bdbdbd' : '#666' }),
				singleValue: (base) => ({ ...base, color: isDark ? '#fff' : '#000' }),
				menu: (base) => ({
					...base,
					backgroundColor: listBg,
					color: isDark ? '#fff' : '#000',
					zIndex: theme.zIndex.tooltip + 1,
				}),
				menuPortal: (base) => ({
					...base,
					zIndex: theme.zIndex.tooltip + 1,
				}),
				menuList: (base) => ({
					...base,
					backgroundColor: listBg,
					color: isDark ? '#fff' : '#000',
				}),
				option: (base, state) => ({
					...base,
					backgroundColor: state.isSelected
						? hoverBg
						: state.isFocused
							? hoverBg
							: listBg,
					color: state.isSelected || state.isFocused ? '#fff' : isDark ? '#fff' : '#000',
					cursor: 'pointer',
				}),
				noOptionsMessage: (base) => ({
					...base,
					backgroundColor: listBg,
					color: isDark ? '#bdbdbd' : '#666',
				}),
				indicatorSeparator: (base) => ({ ...base, backgroundColor: isDark ? '#444' : '#e0e0e0' }),
				dropdownIndicator: (base, state) => ({
					...base,
					color: state.isFocused ? theme.palette.primary.main : (isDark ? '#bbb' : '#666'),
					':hover': { color: theme.palette.primary.main },
				}),
				clearIndicator: (base) => ({ ...base, color: isDark ? '#bbb' : '#666', ':hover': { color: theme.palette.primary.main } }),
			};
		},
		reactSelectTheme: (base) => ({
			...base,
			colors: {
				...base.colors,
				primary: theme.palette.primary.main,
			},
		}),
	};
	return theme;
};
// Components
export const SmartSwitch = styled(Switch)(({ theme }) => {
	const size = pxToRem(20);
	const offset = pxToRem(7);
	const isDark = theme.palette.mode === 'dark';
	const trackColor = isDark ? theme.palette.grey[800] : theme.palette.grey[50];
	const borderColor = isDark ? theme.palette.grey[700] : theme.palette.grey[300];
	const thumbShadow = isDark
		? '0 2px 4px rgba(0,0,0,0.6)'
		: '0 3px 8px rgba(0,0,0,0.15), 0 1px 1px rgba(0,0,0,0.16), 0 3px 1px rgba(0,0,0,0.1)';
	return {
		width: pxToRem(50),
		height: pxToRem(20),
		[`& .${switchClasses.switchBase}`]: {
			left: `${offset} !important`,
			transitionDuration: '300ms',
			[`&.${switchClasses.checked}`]: {
				[`& + .${switchClasses.track}`]: {
					backgroundColor: '#dc5c87',
					opacity: 1,
					border: 'none',
				},
			},
		},
		[`& .${switchClasses.thumb}`]: {
			backgroundColor: theme.palette.common.white,
			width: size,
			height: size,
			boxShadow: thumbShadow,
		},
		[`& .${switchClasses.track}`]: {
			borderRadius: 40,
			border: `1px solid ${borderColor}`,
			backgroundColor: trackColor,
			opacity: 1,
			transition: theme.transitions.create(['background-color', 'border']),
		},
	};
});
export const SmartBadge = styled(Badge)({
	'& .MuiBadge-badge': {
		backgroundColor: '#dc5c87',
		color: '#fff',
	},
});
export const SmartIconButton = styled(IconButton)(({ theme }) => ({
	'&:hover': {
		backgroundColor: 'transparent',
	}
}));
export const SmartDivider = styled(Divider)({
	marginLeft: '3px',
	marginRight: '3px',
	width: '2px',
	background: '#dc5c87',
});
export const SmartTaskCount = styled(Typography)({
	fontFamily: 'Lato, sans-serif',
	color: '#dc5c87',
});
// SmartToolbar styles
export const SmartToolbarContainerFull = {
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	width: '100%',
	flexWrap: 'wrap',
	gap: 1,
};
export const SmartToolbarContainerAuto = {
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	width: 'auto',
	flexWrap: 'wrap',
	gap: 1,
};
export const SmartToolbarGroupLeft = {
	display: 'flex',
	alignItems: 'center',
	gap: 1,
};
export const SmartToolbarGroupCenter = {
	display: 'flex',
	alignItems: 'center',
	gap: 1,
	flexWrap: 'wrap',
};
export const SmartToolbarGroupRight = {
	display: 'flex',
	alignItems: 'center',
	gap: 0,
};
export const SmartAlertBannerContainer = {
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	paddingLeft: 1,
	paddingBottom: 1,
};
export const SmartTableContainerSx = (height) => ({
	maxHeight: height || 500,
	overflowY: 'auto',
});
// Dialog styles
export const SmartDialogLayout = ({ title, table, row, children }) => {
	const theme = useTheme();
	const darkMode = theme.palette.mode === 'dark';
	return (
		<>
			<DialogTitle>
				<Typography variant="h6" textAlign="center">{title}</Typography>
			</DialogTitle>
			<DialogContent
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: '1rem',
					backgroundColor: darkMode ? theme.palette.grey[900] : theme.palette.background.paper,
				}}
			>
				{children}
			</DialogContent>
			<DialogActions
				sx={{
					'& button:first-of-type': {
						color: '#dc5c87',
						border: '1px solid #dc5c87',
					},
					'& button:last-of-type': {
						backgroundColor: '#dc5c87',
						color: '#fff',
						'&:hover': { backgroundColor: '#c94e77' },
					},
				}}
			>
				<MRT_EditActionButtons variant="text" table={table} row={row} />
			</DialogActions>
		</>
	);
};
// Generic styles for SmartTable
export const SmartTitleTypography = styled(Typography)({
	fontFamily: 'Lato, sans-serif',
	mr: 2,
	display: { xs: 'none', md: 'flex' },
	fontWeight: 700,
	letterSpacing: '.3rem',
	color: 'inherit',
	textDecoration: 'none',
});
export const SmartAvatarTitle = styled(Avatar)(({ theme }) => ({
	display: 'flex',
	marginRight: theme.spacing(1),
	width: 32,
	height: 32,
	backgroundColor: 'transparent',

	'& img': {
		objectFit: 'contain'
	}
}));
export const SmartSvgTitle = styled('img')(({ theme }) => ({
	display: 'flex',
	marginRight: theme.spacing(2),
	width: 30,
	height: 30,
	objectFit: 'contain',
	backgroundColor: 'transparent',
}));
export const SmartSvgCustom = styled('img', {
	shouldForwardProp: (prop) => prop !== 'size',
})(({ theme, size, width, height }) => ({
	display: 'flex',
	marginRight: theme.spacing(2),
	width:  width  ?? size ?? 30,
	height: height ?? size ?? 30,
	objectFit: 'contain',
	backgroundColor: 'transparent',
}));
// Action Popup styles
export const SmartActionStack = styled(Stack)({
	mx: 0.75,
	minWidth: 40,
	textAlign: 'center',
});
export const SmartActionLabel = styled(Typography)({
	fontSize: 9,
	fontWeight: 500,
	color: 'text.secondary',
	lineHeight: 1.2,
	maxWidth: 65,
	pl: 1,
	pr: 1,
});
export const SmartCloseFloatingButton = styled(IconButton)(({ theme }) => ({
	position: 'absolute',
	top: -8,
	right: -8,
	width: 24,
	height: 24,
	backgroundColor: '#fff',
	border: '0px solid #dc5c87',
	zIndex: 10,
	boxShadow: theme.shadows[2],
	padding: 0,
	'&:hover': {
		backgroundColor: '#dc5c87',
	},
	'& svg': {
		color: '#dc5c87',
		'&:hover': {
			color: '#fff',
		},
	},
}));
export const SmartFloatingPopupContainer = styled(Box)({
	position: 'fixed',
	bottom: 45,
	left: '50%',
	transform: 'translateX(-50%)',
	zIndex: 1500,
});
export const SmartActionPaper = styled(Paper)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	padding: theme.spacing(1, 2),
	backgroundColor: theme.palette.background.paper,
	position: 'relative',
}));
export const SmartActionAccentBar = styled(Box)({
	width: 3,
	height: 35,
	backgroundColor: '#f8445b',
	marginLeft: '-16px',
	borderTopLeftRadius: 8,
	borderBottomLeftRadius: 8,
});
// Smart Agent Profile styles
export const SmartAgentLabel = styled(Typography)(({ theme }) => ({
	fontSize: '0.75rem',
	color: theme.palette.text.secondary,
	marginBottom: theme.spacing(0.25),
}));
export const SmartAgentText = styled(Typography)(({ theme }) => ({
	fontSize: '0.9rem',
	color: theme.palette.text.primary,
	fontWeight: 500,
}));
export const SmartProfileImage = styled(Box)(({ theme }) => ({
	width: 96,
	height: 96,
	borderRadius: '50%',
	objectFit: 'cover',
	border: `2px solid ${theme.palette.divider}`,
}));
export const SmartAgentContainer = styled(Box)(({ theme }) => ({
	padding: theme.spacing(2),
	display: 'flex',
	alignItems: 'center',
	width: '100%',
}));
// Smart Task Route styles
export const SmartTaskRouteContainer = styled(Box)(({ theme }) => ({
	padding: theme.spacing(2),
	minHeight: 450,
	width: '100%',
}));
export const SmartTaskRouteLabel = styled(Typography)(({ theme }) => ({
	fontSize: '0.75rem',
	color: theme.palette.text.secondary,
	marginBottom: theme.spacing(0.25),
}));
export const SmartTaskRouteInput = styled(TextField)(({ theme }) => ({
	'& .MuiInputBase-root': {
		fontSize: '0.9rem',
		backgroundColor: theme.palette.background.paper,
	},
}));
export const SmartTaskRouteNote = styled(Typography)(({ theme }) => ({
	fontSize: '0.8rem',
	color: theme.palette.error.main,
	marginTop: theme.spacing(1),
}));
